import { useMemo } from 'react';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useContent } from 'src/contexts/ContentContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { ContentId } from 'src/utils/constants/contentId';
import { TAG_OPERATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { ActionOutboxEntityType } from 'src/WebApiController';

import { TagValueFilterInput } from './TagValueFilterInput';
import * as styles from './useTagFiltersBase.css';

export const useTagFiltersBase = (
  tagName: string | null,
  tagValue: string | null,
  tagValues: string[] | null,
  tagOperation: string | null,
  setTagName: (tagName: string | null) => void,
  setTagValue: (tagValue: string | null) => void,
  setTagValues: (tagValues: string[]) => void,
  setTagOperation: (op: string | null) => void,
  titleContentId: ContentId,
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent,
  // Multi-value chip input + operation selector are Advanced Search only.
  // Other surfaces (Event Page, grid, reports) use the single-value input.
  isMultiValue: boolean,
  includeInReport?: boolean,
  disabled?: boolean,
  tagNameFilterId = 'tagName',
  tagValueFilterId = 'tagValue'
): FilterToolbarGroup => {
  const isNoTags = tagOperation === 'NoTags';

  const { tagsMetadata: existingTagsMetadata = [] } = useTagsForEntityType(
    disabled ? undefined : entityType,
    includeInReport
  );

  const noTagsMsg = useContent(ContentId.NoTagsExist);

  const selectedTagMetadata = useMemo(() => {
    return (existingTagsMetadata ?? []).find((r) => r.key === tagName);
  }, [existingTagsMetadata, tagName]);

  const keyOptionsContent = useMemo(() => {
    const keyOptions = (existingTagsMetadata ?? [])
      .toSorted((r1, r2) => r1.key.localeCompare(r2.key))
      .reduce(
        (re, r) => {
          re[r.key] = r.key;
          return re;
        },
        {} as Record<string, string>
      );

    return keyOptions ?? {};
  }, [existingTagsMetadata]);

  const noTagsExist = useMemo(() => {
    return (existingTagsMetadata ?? []).length === 0;
  }, [existingTagsMetadata]);

  const tagNameItem = {
    filterId: tagNameFilterId as FilterToolbarItemId,
    labelContentId: ContentId.Name,
    filterQueryKeys: [tagNameFilterId] as FilterToolbarItemId[],
    filterItem: (
      <PosSelect
        className={styles.selectFull}
        onChange={(newValue: string) => {
          if (tagName !== newValue) {
            setTagName(newValue);
          }
        }}
        value={tagName ?? ''}
        displayText={noTagsExist ? noTagsMsg : (tagName ?? '')}
        searchable
        disabled={noTagsExist}
        valueOptionsContent={keyOptionsContent}
        placeholderText={ContentId.None}
        enableEmptySelection
        shape="rectFull"
      />
    ),
  };

  // Derive the operation query key from the name key so filtersMap lookups in
  // getAppliedFilters match the actual query field (e.g. 'tagOperation',
  // 'eventTagOperation'). A fabricated key like '${tagNameFilterId}Op' is
  // absent from the query object, so the filter group would disappear after
  // Search (not detected as applied).
  const tagOperationFilterId = tagNameFilterId.replace('Name', 'Operation');

  const opItem = isMultiValue
    ? {
        filterId: tagOperationFilterId as unknown as FilterToolbarItemId,
        labelContentId: ContentId.Operation,
        filterQueryKeys: [
          tagOperationFilterId,
        ] as unknown as FilterToolbarItemId[],
        filterItem: (
          <PosSelect
            className={styles.selectFull}
            onChange={(newValue: string) => {
              setTagOperation(newValue || null);
            }}
            value={
              tagOperation && tagOperation in TAG_OPERATION_TO_CID
                ? tagOperation
                : 'Contains'
            }
            valueOptionsContent={TAG_OPERATION_TO_CID}
            shape="rectFull"
          />
        ),
      }
    : null;

  const tagValueItem = {
    filterId: tagValueFilterId as FilterToolbarItemId,
    labelContentId: ContentId.Value,
    filterQueryKeys: [tagValueFilterId] as FilterToolbarItemId[],
    filterItem: (
      <TagValueFilterInput
        valueType={selectedTagMetadata?.valueType}
        disabled={selectedTagMetadata == null || noTagsExist}
        value={tagValue ?? ''}
        values={tagValues ?? undefined}
        onChange={(newValue) => {
          if (tagValue !== newValue) {
            setTagValue(newValue);
          }
        }}
        onValuesChange={setTagValues}
        isMultiValue={isMultiValue}
      />
    ),
  };

  return {
    titleContentId,
    type: 'row',
    // When NoTags is selected, name and value inputs are irrelevant — show only the operation selector.
    items: isNoTags
      ? opItem
        ? [opItem]
        : []
      : [tagNameItem, ...(opItem ? [opItem] : []), tagValueItem],
  };
};
